import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, RotateCcw, Archive } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "../lib/api";
import { invalidateAppSettings } from "../lib/useAppSettings";

const RELATIONS = [["after", "after finish of"], ["concurrent", "concurrent with start of"], ["before", "finish before"]];

/** DEMO-2B — Settings › Scheduling: propose scope dates from the GC schedule. Ships OFF. */
export function GcRulesSection() {
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState([]);
  const [anchors, setAnchors] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [showRetired, setShowRetired] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newScope, setNewScope] = useState("");
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });

  const loadRules = () => api.get("/gc-rules", { params: { include_inactive: true } }).then((r) => { setRules(r.data.rules); setAnchors(r.data.anchors); }).catch(() => {});
  const loadHolidays = () => api.get("/holidays/all").then((r) => setHolidays(r.data || [])).catch(() => {});
  useEffect(() => { api.get("/settings/app").then((r) => setSettings(r.data)).catch(() => {}); loadRules(); loadHolidays(); }, []);

  const save = async (patch, label = "Scheduling settings saved") => {
    try { const r = await api.patch("/settings/app", patch); setSettings(r.data); invalidateAppSettings(); toast.success(label); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const saveRule = async (rule) => {
    try {
      if (rule.id) await api.put(`/gc-rules/${rule.id}`, rule);
      else await api.post("/gc-rules", rule);
      setEditing(null); loadRules(); toast.success("Rule saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const addHoliday = async () => {
    if (!newHoliday.date) { toast.error("Pick a date"); return; }
    try { await api.post("/holidays/custom", newHoliday); setNewHoliday({ date: "", name: "" }); loadHolidays(); toast.success("Holiday added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const retire = async (h) => {
    try { await api.post(`/holidays/${h.id}/retire`, null, { params: { restore: !!h.retired } }); loadHolidays(); }
    catch { toast.error("Update failed"); }
  };
  if (!settings) return null;
  const crew = settings.gc_rule_crew_defaults || {};
  const anchorLabel = (k) => anchors.find((a) => a.key === k)?.label || k;
  const visibleHolidays = holidays.filter((h) => showRetired || !h.retired);

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="settings-gc-rules-section">
      <h3 className="text-sm font-semibold text-gray-900">Scope dates from the GC schedule</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Start from a rule against the GC's anchor activities; finish from budgeted hours ÷ (crew × hours per day) in workdays on the
        company calendar. Proposals only — the PM accepts each row. Ships off.
      </p>
      <label className="mt-3 text-xs text-gray-500 flex items-start gap-2">
        <input type="checkbox" className="mt-0.5" data-testid="settings-gc-rule-proposals"
               checked={!!settings.gc_rule_proposals} onChange={(e) => save({ gc_rule_proposals: e.target.checked })} />
        <span>Propose scope dates from GC schedule<span className="block text-[11px] text-gray-400">off = the Run button is hidden on every job's Schedules tab</span></span>
      </label>

      <div className="mt-3 flex flex-wrap items-end gap-4">
        <label className="text-[11px] text-gray-500">
          Hours per day (default)
          <Input type="number" min={1} max={24} defaultValue={settings.gc_rule_hours_per_day ?? 8} className="h-8 w-24 text-xs mt-0.5"
                 data-testid="settings-gc-rule-hpd"
                 onBlur={(e) => Number(e.target.value) !== Number(settings.gc_rule_hours_per_day) && save({ gc_rule_hours_per_day: Number(e.target.value) }, "Hours per day saved")} />
        </label>
        <label className="text-[11px] text-gray-500">
          Crew size default
          <Input type="number" min={1} defaultValue={crew._default ?? 2} className="h-8 w-24 text-xs mt-0.5" data-testid="settings-gc-rule-crew-default"
                 onBlur={(e) => Number(e.target.value) !== Number(crew._default ?? 2) && save({ gc_rule_crew_defaults: { ...crew, _default: Number(e.target.value) } }, "Crew default saved")} />
        </label>
        <div className="text-[11px] text-gray-500">
          Per-scope crew defaults
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            {Object.entries(crew).filter(([k]) => k !== "_default").sort().map(([code, n]) => (
              <span key={code} className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs" data-testid={`settings-gc-crew-${code}`}>
                <b>{code}</b>
                <input type="number" min={1} defaultValue={n} className="w-10 bg-transparent text-xs tabular-nums"
                       onBlur={(e) => Number(e.target.value) !== Number(n) && save({ gc_rule_crew_defaults: { ...crew, [code]: Number(e.target.value) } }, `${code} crew saved`)} />
                <button type="button" className="text-gray-400" title="Remove"
                        onClick={() => { const next = { ...crew }; delete next[code]; save({ gc_rule_crew_defaults: next }, `${code} crew default removed`); }}>×</button>
              </span>
            ))}
            <Input value={newScope} onChange={(e) => setNewScope(e.target.value.toUpperCase())} placeholder="Scope code" className="h-7 w-28 text-xs" data-testid="settings-gc-crew-new-code" />
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!newScope.trim()} data-testid="settings-gc-crew-add"
                    onClick={() => { save({ gc_rule_crew_defaults: { ...crew, [newScope.trim()]: 2 } }, `${newScope.trim()} crew default added`); setNewScope(""); }}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center gap-2">
          <div className="text-xs font-semibold text-gray-700">Rules</div>
          <Button size="sm" variant="outline" className="h-7 text-xs ml-auto" data-testid="settings-gc-rule-add"
                  onClick={() => setEditing({ label: "", scopes: [], after: [], concurrent: [], before: [], weeks_before: null, offset_days: null, note: "", order: 100, phase: "", scope_group: "" })}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add rule
          </Button>
        </div>
        <table className="w-full text-xs mt-1" data-testid="settings-gc-rules-table">
          <thead className="text-[10px] uppercase text-gray-500"><tr>
            <th className="text-left py-1">#</th><th className="text-left">Scope / group</th><th className="text-left">Anchor · relation</th>
            <th className="text-right">Offset</th><th className="text-left pl-3">Note</th><th />
          </tr></thead>
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className={`border-t border-gray-100 align-top ${r.active === false ? "opacity-50" : ""}`} data-testid={`gc-rule-row-${r.id}`}>
                <td className="py-1 tabular-nums text-gray-400">{r.order}</td>
                <td className="py-1">
                  <div className="font-medium text-gray-800">{r.label}{r.phase && <span className="text-gray-500 font-normal"> · {r.phase}</span>}</div>
                  <div className="text-gray-500">{r.scope_group === "all_other" ? "every scope without its own rule" : (r.scopes || []).join(", ") || <span className="text-amber-700">no scope assigned</span>}</div>
                </td>
                <td className="py-1 text-gray-700">
                  {(r.after || []).map((a) => <div key={a}>after {anchorLabel(a)} finish</div>)}
                  {(r.concurrent || []).map((a) => <div key={a}>concurrent with {anchorLabel(a)} start</div>)}
                  {r.weeks_before?.anchor && <div>{r.weeks_before.weeks} weeks before {anchorLabel(r.weeks_before.anchor)} start</div>}
                  {(r.before || []).map((b, i) => <div key={i} className="text-gray-500">finish before {anchorLabel(b.anchor)} {b.edge || "start"}</div>)}
                </td>
                <td className="py-1 text-right tabular-nums">
                  {r.offset_days == null ? <span className="text-amber-700" title="No offset in the rule — 0 days, rows flagged “defaulted offset”">0 (defaulted)</span> : `${r.offset_days}d`}
                </td>
                <td className="py-1 pl-3 text-gray-500 max-w-[280px]">{r.note}</td>
                <td className="py-1 text-right whitespace-nowrap">
                  <button type="button" className="text-blue-700 hover:underline mr-2" onClick={() => setEditing(r)} data-testid={`gc-rule-edit-${r.id}`}><Pencil className="w-3.5 h-3.5 inline" /></button>
                  <button type="button" className="text-gray-400 hover:text-gray-700" title={r.active === false ? "Restore" : "Deactivate (kept, never deleted)"}
                          onClick={() => saveRule({ id: r.id, active: r.active === false })} data-testid={`gc-rule-toggle-${r.id}`}>
                    {r.active === false ? <RotateCcw className="w-3.5 h-3.5 inline" /> : <Archive className="w-3.5 h-3.5 inline" />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {editing && <RuleEditor rule={editing} anchors={anchors} onCancel={() => setEditing(null)} onSave={saveRule} />}
      </div>

      <div className="mt-4">
        <div className="flex items-center gap-2">
          <div className="text-xs font-semibold text-gray-700">Company holidays</div>
          <span className="text-[11px] text-gray-400">skipped when counting workdays · US federal holidays seeded</span>
          <label className="ml-auto text-[11px] text-gray-500 inline-flex items-center gap-1">
            <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} data-testid="settings-holidays-show-retired" /> show retired
          </label>
        </div>
        <div className="mt-1 flex flex-wrap gap-1.5" data-testid="settings-holidays-list">
          {visibleHolidays.map((h) => (
            <span key={h.id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${h.retired ? "border-gray-200 text-gray-400 line-through" : "border-gray-200 bg-gray-50 text-gray-700"}`}
                  data-testid={`holiday-${h.date}`}>
              <span className="tabular-nums">{h.date}</span> {h.name}
              <button type="button" className="text-gray-400 hover:text-gray-700 ml-0.5" title={h.retired ? "Restore" : "Retire"} onClick={() => retire(h)} data-testid={`holiday-retire-${h.id}`}>
                {h.retired ? <RotateCcw className="w-3 h-3" /> : "×"}
              </button>
            </span>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-2">
          <Input type="date" value={newHoliday.date} onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })} className="h-8 w-40 text-xs" data-testid="settings-holiday-date" />
          <Input value={newHoliday.name} onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })} placeholder="Name" className="h-8 w-48 text-xs" data-testid="settings-holiday-name" />
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={addHoliday} data-testid="settings-holiday-add"><Plus className="w-3.5 h-3.5 mr-1" /> Add holiday</Button>
        </div>
      </div>
    </section>
  );
}

function RuleEditor({ rule, anchors, onCancel, onSave }) {
  const [r, setR] = useState({ ...rule, scopes: rule.scopes || [], after: rule.after || [], concurrent: rule.concurrent || [], before: rule.before || [] });
  const [rel, setRel] = useState({ kind: "after", anchor: anchors[0]?.key || "" });
  const toggleList = (field, key) => setR({ ...r, [field]: r[field].includes(key) ? r[field].filter((k) => k !== key) : [...r[field], key] });
  const addRel = () => {
    if (!rel.anchor) return;
    if (rel.kind === "before") setR({ ...r, before: [...r.before, { anchor: rel.anchor, edge: "start" }] });
    else toggleList(rel.kind, rel.anchor);
  };
  return (
    <div className="mt-2 rounded-md border border-blue-200 bg-blue-50/40 p-3 text-xs space-y-2" data-testid="gc-rule-editor">
      <div className="flex flex-wrap gap-2">
        <label className="text-gray-600">Label<Input value={r.label} onChange={(e) => setR({ ...r, label: e.target.value })} className="h-7 w-64 text-xs mt-0.5" data-testid="gc-rule-label" /></label>
        <label className="text-gray-600">Order<Input type="number" value={r.order ?? 100} onChange={(e) => setR({ ...r, order: Number(e.target.value) })} className="h-7 w-20 text-xs mt-0.5" data-testid="gc-rule-order" /></label>
        <label className="text-gray-600">Scopes (comma-separated)
          <Input value={r.scopes.join(", ")} onChange={(e) => setR({ ...r, scopes: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) })}
                 className="h-7 w-56 text-xs mt-0.5" placeholder="e.g. WP, CG" data-testid="gc-rule-scopes" disabled={r.scope_group === "all_other"} />
        </label>
        <label className="text-gray-600">Phase<Input value={r.phase || ""} onChange={(e) => setR({ ...r, phase: e.target.value })} className="h-7 w-24 text-xs mt-0.5" placeholder="track" data-testid="gc-rule-phase" /></label>
        <label className="text-gray-600">Offset days
          <div className="flex items-center gap-1 mt-0.5">
            <Input type="number" value={r.offset_days ?? ""} placeholder="default 0" onChange={(e) => setR({ ...r, offset_days: e.target.value === "" ? null : Number(e.target.value) })} className="h-7 w-24 text-xs" data-testid="gc-rule-offset" />
            {r.offset_days == null && <span className="text-amber-700">defaulted</span>}
          </div>
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {r.after.map((a) => <Chip key={`a${a}`} text={`after ${a} finish`} onX={() => toggleList("after", a)} />)}
        {r.concurrent.map((a) => <Chip key={`c${a}`} text={`concurrent ${a} start`} onX={() => toggleList("concurrent", a)} />)}
        {r.before.map((b, i) => <Chip key={`b${i}`} text={`before ${b.anchor} ${b.edge || "start"}`} onX={() => setR({ ...r, before: r.before.filter((_, j) => j !== i) })} />)}
        {r.weeks_before?.anchor && <Chip text={`${r.weeks_before.weeks} wk before ${r.weeks_before.anchor} start`} onX={() => setR({ ...r, weeks_before: null })} />}
        <select value={rel.kind} onChange={(e) => setRel({ ...rel, kind: e.target.value })} className="h-7 border border-gray-300 rounded px-1 bg-white" data-testid="gc-rule-rel-kind">
          {RELATIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          <option value="weeks_before">N weeks before start of</option>
        </select>
        <select value={rel.anchor} onChange={(e) => setRel({ ...rel, anchor: e.target.value })} className="h-7 border border-gray-300 rounded px-1 bg-white" data-testid="gc-rule-rel-anchor">
          {anchors.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
        {rel.kind === "weeks_before" && <Input type="number" min={1} value={rel.weeks ?? 3} onChange={(e) => setRel({ ...rel, weeks: Number(e.target.value) })} className="h-7 w-16 text-xs" data-testid="gc-rule-rel-weeks" />}
        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="gc-rule-rel-add"
                onClick={() => (rel.kind === "weeks_before" ? setR({ ...r, weeks_before: { anchor: rel.anchor, weeks: rel.weeks ?? 3 } }) : addRel())}>+ relation</Button>
      </div>
      <label className="block text-gray-600">Note<Input value={r.note || ""} onChange={(e) => setR({ ...r, note: e.target.value })} className="h-7 text-xs mt-0.5" data-testid="gc-rule-note" /></label>
      <div className="flex gap-2">
        <Button size="sm" className="h-7 text-xs" onClick={() => onSave({ ...r, clear_offset: r.offset_days == null })} disabled={!r.label.trim()} data-testid="gc-rule-save">Save rule</Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onCancel} data-testid="gc-rule-cancel">Cancel</Button>
      </div>
    </div>
  );
}

const Chip = ({ text, onX }) => (
  <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-white px-2 py-0.5 text-[11px]">
    {text}<button type="button" onClick={onX} className="text-gray-400">×</button>
  </span>
);
